import React, { useState, useEffect } from 'react';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import axios from 'axios';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Upload,
  Save,
  X,
  Users as UsersIcon,
  FileText,
  BarChart3,
  Calendar,
  Edit,
  Trash2,
  Plus,
  Search,
  Filter,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  Download,
  FileUp, // New icon for bulk upload
  FileSpreadsheet // New icon for CSV
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

const UsersPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
// Edit Modal States
const [editModalOpen, setEditModalOpen] = useState(false);
const [editingUser, setEditingUser] = useState(null);
const [editForm, setEditForm] = useState({
  username: '',
  email: '',
  phone: '',
  password: '' // Add password field
});
const [editLoading, setEditLoading] = useState(false);
const [showPasswordField, setShowPasswordField] = useState(false); // To toggle password field visibility
  
  // Delete Modal States
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Create User Modal States
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    is_member: 0
  });
  const [createLoading, setCreateLoading] = useState(false);

  // ==================== BULK UPLOAD STATES ====================
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null); // { success: true/false, message: '', processed: 0, total: 0 }
  // ==================== END BULK UPLOAD STATES ====================

  const navigate = useNavigate();

  // API base URL
  const apiUrl = process.env.REACT_APP_API_URL;
  const axiosConfig = {
    baseURL: apiUrl,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
      'Content-Type': 'application/json'
    }
  };

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/auth/users', axiosConfig);
      
      if (response.data.success) {
        setUsers(response.data.data);
      } else {
        toast.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(error.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Handle body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [sidebarOpen]);

  // Filter users based on search and status
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm);
    
    const matchesStatus = 
      filterStatus === 'all' || 
      (filterStatus === 'active' && user.status === 'active') ||
      (filterStatus === 'inactive' && user.status === 'inactive');
    
    return matchesSearch && matchesStatus;
  });

  // Handle row click to navigate to content access page
  const handleRowClick = (userId) => {
    navigate(`/admin/users/content-access/${userId}`);
  };

  // Open Edit Modal
  const openEditModal = (user, e) => {
    e.stopPropagation();
    setEditingUser(user);
    setEditForm({
      username: user.username || '',
      email: user.email || '',
      phone: user.phone || '',
      password: '' // Always start with empty password
    });
    setShowPasswordField(false); // Hide password field initially
    setEditModalOpen(true);
  };

  // Handle Edit Form Change
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Edit Submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    try {
      setEditLoading(true);
      
      // Only include password in request if it's not empty
      const updateData = {
        username: editForm.username,
        email: editForm.email,
        phone: editForm.phone
      };
      
      // Only add password to request if it's provided and not empty
      if (editForm.password && editForm.password.trim() !== '') {
        updateData.password = editForm.password;
      }
      
      const response = await axios.put(
        `/api/auth/users/${editingUser.id}`,
        updateData,
        axiosConfig
      );
      
      if (response.data.success) {
        setUsers(users.map(user => 
          user.id === editingUser.id ? { ...user, ...response.data.data } : user
        ));
        toast.success('User updated successfully');
        setEditModalOpen(false);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.error || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  // Open Delete Modal
  const openDeleteModal = (user, e) => {
    e.stopPropagation();
    setDeletingUser(user);
    setDeleteModalOpen(true);
  };

  // Handle Delete User
  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    
    try {
      setDeleteLoading(true);
      const response = await axios.delete(
        `/api/auth/users/${deletingUser.id}`,
        axiosConfig
      );
      
      if (response.data.success) {
        setUsers(users.filter(user => user.id !== deletingUser.id));
        toast.success('User deleted successfully');
        setDeleteModalOpen(false);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
      setDeletingUser(null);
    }
  };

  // Handle Create Form Change
  const handleCreateChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCreateForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  // Handle Create User
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setCreateLoading(true);
      
      const response = await axios.post(
        '/api/auth/create-user-directly',
        {
          username: createForm.username,
          email: createForm.email,
          phone: createForm.phone,
          password: createForm.password,
          is_member: createForm.is_member
        },
        axiosConfig
      );
      
      if (response.data.success) {
        setUsers(prevUsers => [response.data.data, ...prevUsers]);
        setCreateForm({
          username: '',
          email: '',
          phone: '',
          password: '',
          is_member: 0
        });
        toast.success('User created successfully!');
        setCreateModalOpen(false);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.error || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  // ==================== BULK UPLOAD FUNCTIONS ====================
  // Download sample CSV file
  const downloadSampleCSV = () => {
    // CSV headers
    const headers = ['username', 'email', 'phone', 'password', 'is_member'];
    
    // Sample data rows
    const sampleData = [
      ['john_doe', 'john@example.com', '9319745997', 'Password@123', '0'],
      ['jane_smith', 'jane@example.com', '9876543211', 'Password@123', '1'],
      ['alex_wilson', 'alex@example.com', '9876543212', 'Password@123', '0'],
      ['sara_jones', 'sara@example.com', '9876543213', 'Password@123', '1']
    ];
    
    // Create CSV content
    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += headers.join(',') + '\n';
    sampleData.forEach(row => {
      csvContent += row.join(',') + '\n';
    });
    
    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'user_bulk_upload_sample.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Sample CSV file downloaded!');
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
      setUploadStatus(null); // Reset previous status
    }
  };

  // Parse CSV file
  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const lines = content.split('\n');
          
          // Parse CSV rows
          const users = [];
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
          
          // Validate headers
          const requiredHeaders = ['username', 'email', 'phone', 'password'];
          const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
          
          if (missingHeaders.length > 0) {
            reject(new Error(`Missing required columns: ${missingHeaders.join(', ')}`));
            return;
          }
          
          // Process data rows
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim() === '') continue;
            
            const values = lines[i].split(',').map(v => v.trim());
            const user = {};
            
            headers.forEach((header, index) => {
              user[header] = values[index] || '';
            });
            
            // Basic validation
            if (user.username && user.email && user.phone && user.password) {
              user.is_member = user.is_member === '1' || user.is_member?.toLowerCase() === 'true' ? 1 : 0;
              users.push(user);
            }
          }
          
          if (users.length === 0) {
            reject(new Error('No valid user data found in CSV'));
            return;
          }
          
          resolve(users);
        } catch (error) {
          reject(new Error('Error parsing CSV file: ' + error.message));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      reader.readAsText(file);
    });
  };

  // Handle bulk upload
  const handleBulkUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file first');
      return;
    }
    
    try {
      setUploadLoading(true);
      setUploadProgress(0);
      setUploadStatus(null);
      
      // Parse CSV file
      const usersData = await parseCSV(selectedFile);
      
      // Show processing status
      setUploadStatus({
        success: null,
        message: `Found ${usersData.length} users to process...`,
        processed: 0,
        total: usersData.length
      });
      
      // Upload users in batches
      const BATCH_SIZE = 10;
      const results = {
        success: 0,
        failed: 0,
        errors: []
      };
      
      for (let i = 0; i < usersData.length; i += BATCH_SIZE) {
        const batch = usersData.slice(i, i + BATCH_SIZE);
        
        try {
          // Create all users in the batch
          const batchPromises = batch.map(user => 
            axios.post('/api/auth/create-user-directly', user, axiosConfig)
              .then(res => ({ success: true, data: res.data.data }))
              .catch(err => ({ 
                success: false, 
                error: err.response?.data?.error || 'Failed to create user',
                user
              }))
          );
          
          const batchResults = await Promise.all(batchPromises);
          
          // Update progress
          const processed = Math.min(i + BATCH_SIZE, usersData.length);
          setUploadProgress(Math.round((processed / usersData.length) * 100));
          
          // Update status
          const successful = batchResults.filter(r => r.success).length;
          const failed = batchResults.filter(r => !r.success).length;
          
          results.success += successful;
          results.failed += failed;
          
          // Collect errors
          batchResults.filter(r => !r.success).forEach(r => {
            results.errors.push({
              user: r.user,
              error: r.error
            });
          });
          
          // Update status message
          setUploadStatus({
            success: null,
            message: `Processing... ${processed}/${usersData.length} users`,
            processed: processed,
            total: usersData.length
          });
          
          // Small delay between batches
          await new Promise(resolve => setTimeout(resolve, 500));
          
        } catch (batchError) {
          console.error('Batch error:', batchError);
          results.failed += batch.length;
          results.errors.push({
            user: null,
            error: 'Batch processing error'
          });
        }
      }
      
      // Final status
      const success = results.failed === 0;
      setUploadStatus({
        success: success,
        message: success 
          ? `Successfully uploaded ${results.success} users!`
          : `Uploaded ${results.success} users, ${results.failed} failed.`,
        processed: usersData.length,
        total: usersData.length
      });
      
      setUploadProgress(100);
      
      if (success) {
        toast.success(`Successfully created ${results.success} users`);
        // Refresh user list
        fetchUsers();
        // Reset and close modal after delay
        setTimeout(() => {
          setBulkUploadModalOpen(false);
          setSelectedFile(null);
          setUploadStatus(null);
        }, 2000);
      } else {
        toast.warning(`Created ${results.success} users, ${results.failed} failed`);
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({
        success: false,
        message: error.message || 'Upload failed',
        processed: 0,
        total: 0
      });
      toast.error(error.message || 'Failed to process CSV file');
    } finally {
      setUploadLoading(false);
    }
  };

  // Reset bulk upload modal
  const resetBulkUpload = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadStatus(null);
    setBulkUploadModalOpen(false);
  };
  // ==================== END BULK UPLOAD FUNCTIONS ====================

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800';
      case 'deleted':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get member badge class
  const getMemberBadgeClass = (isMember) => {
    return isMember === 1 
      ? 'bg-blue-100 text-blue-800' 
      : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <ToastContainer position="bottom-right"autoClose={3000} />
      
      <div className="flex h-screen">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} />
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          {/* Header */}
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Page Header */}
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                    User Management
                  </h1>
                  <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1 md:mt-2">
                    Manage all registered users in the system
                  </p>
                </div>
                
                <div className="flex flex-col md:flex-row gap-3 mt-4 md:mt-0">
                  {/* Bulk Upload Button */}
                  <button
                    onClick={() => setBulkUploadModalOpen(true)}
                    className="flex items-center justify-center px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <FileUp className="h-5 w-5 mr-2" />
                    Bulk Upload
                  </button>
                  
                  
                  {/* Create Single User Button */}
                  <button
                    onClick={() => setCreateModalOpen(true)}
                    className="flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Create User
                  </button>
                  {/* Bulk Upload Button */}
                  <button
                    onClick={() => navigate('/admin/user/login-history')}
                    className="flex items-center justify-center px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-300 shadow-md hover:shadow-lg "
                  >
                    <FileUp className="h-5 w-5 mr-2" />
                    Login History
                  </button>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 md:p-6 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search Input */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users by name, email, or phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                {/* Status Filter */}
                <div className="w-full md:w-48">
                  <div className="relative">
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center p-8">
                  <UsersIcon className="h-16 w-16 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No users found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {searchTerm || filterStatus !== 'all' 
                      ? 'Try adjusting your search or filter' 
                      : 'No users have been registered yet'}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setCreateModalOpen(true)}
                      className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Create User
                    </button>
                    <button
                      onClick={() => setBulkUploadModalOpen(true)}
                      className="flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <FileUp className="h-5 w-5 mr-2" />
                      Bulk Upload
                    </button>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Member
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Joined
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Access
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredUsers.map((user) => (
                        <tr 
                          key={user.id} 
                          onClick={() => handleRowClick(user.id)}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer group"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                                <User className="h-6 w-6 text-white" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {user.username || 'N/A'}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  ID: {user.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="space-y-1">
                              <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                                <Mail className="h-4 w-4 mr-2 text-gray-400" />
                                {user.email || 'N/A'}
                              </div>
                              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                <Phone className="h-4 w-4 mr-2 text-gray-400" />
                                {user.phone || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(user.status)}`}>
                              {user.status === 'active' && <CheckCircle className="h-3 w-3 mr-1" />}
                              {user.status === 'inactive' && <Clock className="h-3 w-3 mr-1" />}
                              {user.status === 'deleted' && <XCircle className="h-3 w-3 mr-1" />}
                              {user.status?.charAt(0).toUpperCase() + user.status?.slice(1) || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getMemberBadgeClass(user.is_member)}`}>
                              {user.is_member === 1 ? 'Premium Member' : 'Basic Member'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(user.created_at)}
                            {user.last_login && (
                              <div className="text-xs text-gray-400">
                                Last login: {formatDate(user.last_login)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => openEditModal(user, e)}
                                className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </button>
                              <button
                                onClick={(e) => openDeleteModal(user, e)}
                                className="inline-flex items-center px-3 py-1.5 text-sm bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                                disabled={user.status === 'deleted'}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                {user.status === 'deleted' ? 'Deleted' : 'Delete'}
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(user.id);
                              }}
                              className="inline-flex items-center px-3 py-1.5 text-sm bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors group-hover:opacity-100"
                              title="Manage Content Access"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Access
                              <ArrowRight className="h-4 w-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>

          {/* Footer */}
          <Footer />
        </div>
      </div>

      {/* ==================== BULK UPLOAD MODAL ==================== */}
      {bulkUploadModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Bulk User Upload
                </h2>
                <button
                  onClick={resetBulkUpload}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  disabled={uploadLoading}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Download Sample Section */}
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
                  <div className="flex items-center gap-3 mb-3">
                    <FileSpreadsheet className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <h3 className="font-medium text-blue-800 dark:text-blue-300">
                      Download Sample CSV
                    </h3>
                  </div>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mb-3">
                    Download a sample CSV file with the correct format. 
                    Required columns: username, email, phone, password, is_member
                  </p>
                  <button
                    onClick={downloadSampleCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-lg border border-blue-200 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-700 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Download Sample
                  </button>
                </div>

                {/* File Upload Section */}
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 text-center">
                  {selectedFile ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-center gap-3">
                        <FileSpreadsheet className="h-8 w-8 text-green-500" />
                        <div className="text-left">
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {selectedFile.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {(selectedFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedFile(null)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove File
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-700 dark:text-gray-300 mb-2">
                        Drop your CSV file here or click to browse
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        Supports .csv files up to 5MB
                      </p>
                      <label className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
                        Choose File
                        <input
                          type="file"
                          accept=".csv"
                          onChange={handleFileSelect}
                          className="hidden"
                          disabled={uploadLoading}
                        />
                      </label>
                    </div>
                  )}
                </div>

                {/* Upload Progress */}
                {uploadStatus && (
                  <div className={`p-4 rounded-lg ${
                    uploadStatus.success === true 
                      ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                      : uploadStatus.success === false 
                      ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                      : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-medium ${
                        uploadStatus.success === true 
                          ? 'text-green-700 dark:text-green-300' 
                          : uploadStatus.success === false 
                          ? 'text-red-700 dark:text-red-300'
                          : 'text-blue-700 dark:text-blue-300'
                      }`}>
                        {uploadStatus.message}
                      </span>
                      {uploadStatus.total > 0 && (
                        <span className="text-sm text-gray-500">
                          {uploadStatus.processed}/{uploadStatus.total}
                        </span>
                      )}
                    </div>
                    
                    {uploadProgress > 0 && (
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                        <div 
                          className={`h-full rounded-full transition-all duration-300 ${
                            uploadStatus.success === true 
                              ? 'bg-green-500' 
                              : uploadStatus.success === false 
                              ? 'bg-red-500'
                              : 'bg-blue-500'
                          }`}
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    )}
                    
                    {uploadStatus.total > 0 && (
                      <div className="text-right">
                        <span className="text-xs text-gray-500">
                          {uploadProgress}% complete
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={resetBulkUpload}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    disabled={uploadLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkUpload}
                    disabled={!selectedFile || uploadLoading}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Uploading...
                      </>
                    ) : (
                      'Upload & Create Users'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Edit User
                </h2>
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleEditSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={editForm.username}
                      onChange={handleEditChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={editForm.email}
                      onChange={handleEditChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={editForm.phone}
                      onChange={handleEditChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Password Update Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    {!showPasswordField ? (
                      <button
                        type="button"
                        onClick={() => setShowPasswordField(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                      >
                        + Change Password
                      </button>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            New Password
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              setShowPasswordField(false);
                              setEditForm(prev => ({ ...prev, password: '' }));
                            }}
                            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                        <input
                          type="password"
                          name="password"
                          value={editForm.password}
                          onChange={handleEditChange}
                          placeholder="Leave empty to keep current password"
                          minLength="8"
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {editForm.password && editForm.password.length < 8 && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                            Password must be at least 8 characters
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setEditModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    disabled={editLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading || (editForm.password && editForm.password.length < 8)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2 inline-block" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center mb-6">
                <div className="h-12 w-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>

              <h2 className="text-xl font-bold text-center text-gray-900 dark:text-white mb-2">
                Delete User
              </h2>
              
              <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                Are you sure you want to delete <span className="font-semibold text-gray-900 dark:text-white">{deletingUser?.username}</span>?
                This action cannot be undone.
              </p>

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 dark:text-red-300">
                    This will mark the user as deleted. Their account will be deactivated immediately.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setDeletingUser(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={deleteLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Deleting...
                    </>
                  ) : (
                    'Delete User'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Create New User
                </h2>
                <button
                  onClick={() => setCreateModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleCreateSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={createForm.username}
                      onChange={handleCreateChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter username"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={createForm.email}
                      onChange={handleCreateChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="user@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={createForm.phone}
                      onChange={handleCreateChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="10-digit phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={createForm.password}
                      onChange={handleCreateChange}
                      required
                      minLength="8"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Minimum 8 characters"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_member"
                      name="is_member"
                      checked={createForm.is_member === 1}
                      onChange={handleCreateChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_member" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Premium Member
                    </label>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>Note:</strong> The user will be created immediately without OTP verification.
                    They can use their email/phone and password to login right away.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setCreateModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    disabled={createLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2 inline-block" />
                        Create User
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;